import React, { useState } from 'react';

const SeatingChart = ({ onSeatSelection, selectedSeats }) => {
  const [hoveredSeat, setHoveredSeat] = useState(null);

  // Specific seats that are unavailable (taken)
  // Row 1: seats 5-10 unavailable, Row 2: seat 13 unavailable
  const unavailableSeats = [
    '1-5', '1-6', '1-7', '1-8', '1-9', '1-10',
    '2-13'
  ];

  // Generate seating layout
  const generateSeating = () => {
    const seating = [];
    
    // VIP rows (first 2 rows, 15 seats each)
    for (let row = 1; row <= 2; row++) {
      const rowSeats = [];
      for (let seat = 1; seat <= 15; seat++) {
        const seatId = `${row}-${seat}`;
        rowSeats.push({
          row: row,
          seat: seat,
          type: 'VIP Experience',
          price: 70,
          available: !unavailableSeats.includes(seatId),
          id: seatId
        });
      }
      seating.push(rowSeats);
    }
    
    // Premium rows (next 2 rows, 12 seats each)
    for (let row = 3; row <= 4; row++) {
      const rowSeats = [];
      for (let seat = 1; seat <= 12; seat++) {
        const seatId = `${row}-${seat}`;
        rowSeats.push({
          row: row,
          seat: seat,
          type: 'Premium Experience',
          price: 50,
          available: !unavailableSeats.includes(seatId),
          id: seatId
        });
      }
      seating.push(rowSeats);
    }
    
    // Standard rows (last 6 rows, 13 seats each)
    for (let row = 5; row <= 10; row++) {
      const rowSeats = [];
      for (let seat = 1; seat <= 13; seat++) {
        const seatId = `${row}-${seat}`;
        rowSeats.push({
          row: row,
          seat: seat,
          type: 'Standard Experience',
          price: 35,
          available: !unavailableSeats.includes(seatId),
          id: seatId
        });
      }
      seating.push(rowSeats);
    }
    
    return seating;
  };

  const [seating] = useState(generateSeating());

  const handleSeatClick = (seatData) => {
    if (!seatData.available) return;

    const isSelected = selectedSeats.some(seat => seat.id === seatData.id);
    let newSelectedSeats;

    if (isSelected) {
      // Remove seat
      newSelectedSeats = selectedSeats.filter(seat => seat.id !== seatData.id);
    } else {
      // Add seat
      newSelectedSeats = [...selectedSeats, seatData];
    }

    onSeatSelection(newSelectedSeats);
  };

  const getSeatClass = (seatData) => {
    const isSelected = selectedSeats.some(seat => seat.id === seatData.id);
    
    if (!seatData.available) {
      return 'bg-gray-600 cursor-not-allowed opacity-50';
    }
    
    if (isSelected) {
      return 'bg-purple-600 hover:bg-purple-700 cursor-pointer transform scale-110';
    }
    
    switch (seatData.type) {
      case 'VIP Experience':
        return 'bg-yellow-600 hover:bg-yellow-500 cursor-pointer hover:transform hover:scale-110';
      case 'Premium Experience':
        return 'bg-blue-600 hover:bg-blue-500 cursor-pointer hover:transform hover:scale-110';
      default:
        return 'bg-green-600 hover:bg-green-500 cursor-pointer hover:transform hover:scale-110';
    }
  };

  return (
    <div className="relative">
      {/* Stage */}
      <div className="mb-8 text-center">
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 py-4 px-8 rounded-lg mx-auto max-w-md">
          <h3 className="text-white font-bold text-lg">🎭 STAGE 🎭</h3>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 flex flex-wrap justify-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-600 rounded"></div>
          <span>VIP Experience ($70)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-600 rounded"></div>
          <span>Premium Experience ($50)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-600 rounded"></div>
          <span>Standard Experience ($35)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-600 rounded"></div>
          <span>Selected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-600 rounded opacity-50"></div>
          <span>Unavailable</span>
        </div>
      </div>

      {/* Seating Chart */}
      <div className="bg-gray-900/30 p-6 rounded-lg border border-gray-700">
        <div className="space-y-4">
          {seating.map((row) => (
            <div key={row[0].row} className="flex justify-center items-center gap-1">
              {/* Row Label */}
              <div className="w-8 text-center text-gray-400 font-semibold text-sm">
                {row[0].row}
              </div>
              
              {/* Seats */}
              <div className="flex gap-1">
                {row.map((seat, seatIndex) => (
                  <button
                    key={seat.id}
                    onClick={() => handleSeatClick(seat)}
                    onMouseEnter={() => setHoveredSeat(seat)}
                    onMouseLeave={() => setHoveredSeat(null)}
                    disabled={!seat.available}
                    className={`
                      w-8 h-8 rounded transition-all duration-200 text-xs font-bold text-white
                      ${getSeatClass(seat)}
                    `}
                    title={seat.available ? `Row ${seat.row}, Seat ${seat.seat} - ${seat.type} - $${seat.price}` : 'Unavailable'}
                  >
                    {seat.seat}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hover Tooltip */}
      {hoveredSeat && (
        <div className="fixed z-50 bg-black border border-gray-600 rounded-lg p-3 pointer-events-none transform -translate-x-1/2 -translate-y-full"
             style={{
               left: '50%',
               top: '20%',
             }}>
          <div className="text-white text-sm">
            <div className="font-bold">Row {hoveredSeat.row}, Seat {hoveredSeat.seat}</div>
            <div className="text-purple-400">{hoveredSeat.type}</div>
            <div className="text-green-400 font-semibold">${hoveredSeat.price}</div>
            <div className={`text-xs ${hoveredSeat.available ? 'text-green-400' : 'text-red-400'}`}>
              {hoveredSeat.available ? 'Available' : 'Unavailable'}
            </div>
          </div>
        </div>
      )}

      {/* Selection Summary */}
      {selectedSeats.length > 0 && (
        <div className="mt-6 p-4 bg-purple-900/20 rounded-lg border border-purple-500">
          <h4 className="font-bold mb-2">Selected Seats ({selectedSeats.length}):</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {selectedSeats.map((seat) => (
              <div key={seat.id} className="bg-gray-800 p-2 rounded">
                Row {seat.row}, Seat {seat.seat}
                <div className="text-xs text-purple-400">{seat.type}</div>
                <div className="text-xs font-bold">${seat.price}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatingChart;